/* eslint-disable camelcase */
import React, { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  Badge,
  List,
  Pagination,
  Stack,
  Text,
  Card,
  Tooltip,
  useTranslation,
  Trans,
  Spacer,
} from '@piwikpro/ui-components';
import {
  ConfigService,
  errorBoundary,
  InternalListLink,
  Pagination as PaginationManager,
  PermissionSelect,
  withInject,
} from '@piwikpro/platform';
import { useScopedPreference } from '@piwikpro/user-preferences';
import {
  AccessControlService,
  PermissionLevel,
  ResourcePermissionLevel,
  UserAppPermissionItem,
  UserMetaSitePermissionItem,
} from '@piwikpro/access-control-crate';
import { AccessControlState } from '@piwikpro/access-control-crate/redux';
import { AdministrationState } from '../../../../../redux';
import UserPermissionsFilters, {
  emptyPermissionFilters,
  PermissionFilters,
} from './UserPermissionsFilters';
import { permissionSelectOptions } from '../../../../permissionOptions';

export type UserPermissionsListKind = 'app' | 'meta-site';

const PREFERENCE_ID = 'UserPermissions';

const SORT_FIELDS = {
  name: 'name',
  userPermission: 'user_permission',
  groupPermission: 'effective_group_permission',
  overallPermission: 'effective_permission',
} as const;

const PERMISSION_BADGE_COLOR: Record<ResourcePermissionLevel, NonNullable<React.ComponentProps<typeof Badge>['color']>> = {
  manage: 'green',
  'edit-publish': 'light-green',
  edit: 'light-green',
  view: 'light-blue',
  'no-access': 'light-gray',
};

type PermissionItem = UserAppPermissionItem | UserMetaSitePermissionItem;

const permissionLabel = (
  permissionLevels: PermissionLevel[],
  level: ResourcePermissionLevel | null,
) => {
  if (!level) {
    return '';
  }

  const match = permissionLevels.find(item => item.id === level);

  return match ? match.name : level;
};

const nextSort = (current: string, field: string) => (
  current === field ? `-${field}` : field
);

const sortState = (sort: string, field: string): { selected: boolean, order?: 'asc' | 'dsc' } => {
  if (sort === field) {
    return { selected: true, order: 'asc' };
  }

  if (sort === `-${field}`) {
    return { selected: true, order: 'dsc' };
  }

  return { selected: false };
};

const resourceId = (item: PermissionItem) => (
  'app_id' in item ? item.app_id : item.meta_site_id
);

const resourceName = (item: PermissionItem) => (
  'app_name' in item ? item.app_name : item.meta_site_name
);

const SortableColumnHeader = ({
  text,
  field,
  sort,
  onToggle,
}: {
  text: string
  field: string
  sort: string
  onToggle: (field: string) => void
}) => (
  <List.SortableHeader
    text={text}
    onClick={() => onToggle(field)}
    {...sortState(sort, field)}
  />
);

const GroupPermission = ({
  item,
  permissionLevels,
}: {
  item: PermissionItem
  permissionLevels: PermissionLevel[]
}) => {
  if (item.effective_group_permission === 'no-access') {
    return <Text as="p">—</Text>;
  }

  const label = permissionLabel(permissionLevels, item.effective_group_permission);

  if (item.related_group_names.length === 0) {
    return <Text as="p" weight={600}>{label}</Text>;
  }

  return (
    <Text as="p">
      <Trans
        i18nKey="apps.details.permissions.groupPermission.label"
        values={{
          permissionName: label,
          permissionGroup: item.related_group_names.join(', '),
        }}
        components={[
          <Text weight={600} dataId="TextStyle">permissionName</Text>,
        ]}
      />
    </Text>
  );
};

const listColumns = (
  labels: {
    name: string
    userPermission: string
    groupPermission: string
    overallPermission: string
    overallPermissionInfo: string
  },
  sort: string,
  onToggle: (field: string) => void,
) => [
  {
    key: 'name',
    size: 3,
    align: 'left' as const,
    name: (
      <SortableColumnHeader
        text={labels.name}
        field={SORT_FIELDS.name}
        sort={sort}
        onToggle={onToggle}
      />
    ),
  },
  {
    key: 'userPermission',
    size: 3,
    align: 'left' as const,
    name: (
      <SortableColumnHeader
        text={labels.userPermission}
        field={SORT_FIELDS.userPermission}
        sort={sort}
        onToggle={onToggle}
      />
    ),
  },
  {
    key: 'groupPermission',
    size: 4,
    align: 'left' as const,
    name: (
      <SortableColumnHeader
        text={labels.groupPermission}
        field={SORT_FIELDS.groupPermission}
        sort={sort}
        onToggle={onToggle}
      />
    ),
  },
  {
    key: 'overallPermission',
    size: 2,
    align: 'left' as const,
    name: (
      <Tooltip
        title={labels.overallPermission}
        content={labels.overallPermissionInfo}
        position="top"
      >
        <SortableColumnHeader
          text={labels.overallPermission}
          field={SORT_FIELDS.overallPermission}
          sort={sort}
          onToggle={onToggle}
        />
      </Tooltip>
    ),
  },
];

type State = AdministrationState & AccessControlState;

const mapStateToProps = (state: State, { kind }: OwnProps) => {
  const list = kind === 'app'
    ? state.accessControl.user.appsPermissions
    : state.accessControl.user.metaSitesPermissions;

  return {
    user: state.users.details.user.details,
    items: list.items,
    total: list.total,
    isLoading: list.isLoading,
    failed: list.failed,
  };
};

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  accessControl: AccessControlService
  config: ConfigService
  appPermissionLevels: PermissionLevel[]
  metaSitePermissionLevels: PermissionLevel[]
}

interface OwnProps {
  kind: UserPermissionsListKind
  membershipRevision: number
}

type Props = OwnProps & ReduxProps & InjectedProps;

const ResourceName = ({
  kind,
  id,
  name,
}: {
  kind: UserPermissionsListKind
  id: string
  name: string
}) => {
  if (kind === 'app') {
    return (
      <InternalListLink
        to="/administration/apps/$appId"
        params={{ appId: id }}
        label={name}
      />
    );
  }

  return (
    <InternalListLink
      to="/administration/meta-sites/$metaSiteId"
      params={{ metaSiteId: id }}
      label={name}
    />
  );
};

const UserPermissionsList = ({
  kind,
  user,
  items,
  total,
  isLoading,
  failed,
  accessControl,
  config,
  appPermissionLevels,
  metaSitePermissionLevels,
  membershipRevision,
}: Props) => {
  const { t } = useTranslation('administration');
  const levels = kind === 'app' ? appPermissionLevels : metaSitePermissionLevels;
  const dataId = kind === 'app' ? 'user-app-permissions' : 'user-meta-site-permissions';
  const pagePreference = useScopedPreference(PREFERENCE_ID, dataId);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<PermissionFilters>(emptyPermissionFilters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => pagePreference.readStoredLimit());
  const [sort, setSort] = useState<string>(`-${SORT_FIELDS.overallPermission}`);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userId = user?.id ?? '';
  const pagination = PaginationManager.fromQueryParams(items as UserMetaSitePermissionItem[], total, { page, limit });
  const { offset } = pagination;
  const pageLimit = pagination.getLimit();

  useEffect(() => () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    const query = {
      offset,
      limit: pageLimit,
      search,
      sort,
      userPermission: filters.userPermission,
      groupPermission: filters.groupPermission,
      overallPermission: filters.overallPermission,
    };
    const request = kind === 'app'
      ? accessControl.fetchUserAppPermissions(userId, query)
      : accessControl.fetchUserMetaSitePermissions(userId, query);

    request.catch(() => {});
  }, [accessControl, filters, kind, membershipRevision, offset, pageLimit, search, sort, userId]);

  const toggleSort = (field: string) => {
    setSort(current => nextSort(current, field));
    setPage(1);
  };

  const handleSearchChange = (event: { target: { value: string | number } }) => {
    const value = String(event.target.value);

    setSearchInput(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, config.get<number>('LIST_SEARCH_DELAY'));
  };

  const clearSearch = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const savePermission = async (
    resourceIdValue: string,
    permission: ResourcePermissionLevel,
    api: { setSubmitting: (value: boolean) => void, resetForm: () => void },
  ) => {
    try {
      await (kind === 'app'
        ? accessControl.updateUserAppPermission(resourceIdValue, userId, permission, { userUpdatedToast: true })
        : accessControl.updateUserMetaSitePermission(resourceIdValue, userId, permission, { userUpdatedToast: true }));
    } catch (err) {
      api.resetForm();
    }

    api.setSubmitting(false);
  };

  const applyFilters = (nextFilters: PermissionFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const columns = listColumns({
    name: kind === 'app'
      ? t('users.permissions.sites.column.name')
      : t('users.permissions.metaSites.column.name'),
    userPermission: t('users.permissions.column.userPermission'),
    groupPermission: t('users.permissions.column.highestGroupPermission'),
    overallPermission: t('users.permissions.column.overallPermission'),
    overallPermissionInfo: t('users.permissions.column.overallPermission.info'),
  }, sort, toggleSort);

  return (
    <Card.Section subdued dataId={dataId}>
      <Stack vertical spacing="extraNarrow">
        <Stack.Item>
          <Text as="h3" size={16} weight={600} dataId="Label.large">
            {kind === 'app'
              ? t('users.permissions.sites.title')
              : t('users.permissions.metaSites.title')}
          </Text>
        </Stack.Item>
        <Stack.Item>
          <Text as="p" color="InkLighter">
            {kind === 'app'
              ? t('users.permissions.sites.description')
              : t('users.permissions.metaSites.description')}
          </Text>
        </Stack.Item>
        <Stack.Item previousSpacing="default" fill>
          <UserPermissionsFilters
            dataId={dataId}
            kind={kind}
            levels={levels}
            filters={filters}
            search={searchInput}
            searchPlaceholder={kind === 'app'
              ? t('users.permissions.sites.search.placeholder')
              : t('users.permissions.metaSites.search.placeholder')}
            onSearchChange={handleSearchChange}
            onSearchClear={clearSearch}
            onFilterApply={applyFilters}
          />
          <List
            columns={columns}
            isLoading={isLoading}
            error={failed}
          >
            {items.map((item: PermissionItem) => (
              <List.Item key={resourceId(item)}>
                <ResourceName
                  kind={kind}
                  id={resourceId(item)}
                  name={resourceName(item)}
                />
                <PermissionSelect
                  initialValue={item.user_permission}
                  options={permissionSelectOptions(levels, t, kind)}
                  dataId={`${dataId}-permission-${resourceId(item)}`}
                  onSubmit={(values, api) => savePermission(
                    resourceId(item),
                    values.permission as ResourcePermissionLevel,
                    api,
                  )}
                />
                <GroupPermission item={item} permissionLevels={levels} />
                <Badge
                  text={permissionLabel(levels, item.effective_permission)}
                  color={PERMISSION_BADGE_COLOR[item.effective_permission]}
                  size="medium"
                />
              </List.Item>
            ))}
          </List>
          <Spacer size={1} />
          {!isLoading && !failed && (
            <Pagination
              startIndex={pagination.offset}
              endIndex={pagination.offset + pagination.getLimit()}
              limit={pagination.getLimit()}
              total={total}
              limitOptions={config.get<number[]>('PAGINATION_ITEMS_PER_PAGE_OPTIONS')}
              onPageChange={(nextOffset: number) => setPage(pagination.getPageFromOffset(nextOffset))}
              onLimitChange={(nextLimit: number) => {
                setLimit(nextLimit);
                pagePreference.set('limit', nextLimit);
                setPage(1);
              }}
              dataId={`${dataId}-pagination`}
            />
          )}
        </Stack.Item>
      </Stack>
    </Card.Section>
  );
};

export default connector(withInject<InjectedProps>({
  accessControl: 'AccessControlCrate.accessControl',
  config: 'config',
  appPermissionLevels: 'appPermissionLevels',
  metaSitePermissionLevels: 'metaSitePermissionLevels',
})(errorBoundary(UserPermissionsList)));
